import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X, Eye, EyeOff } from "lucide-react";
import api from "@/lib/api";
import { Switch } from "@/components/ui/switch";

const emptyItem = { label: "", path: "", order: 0, visible: true };

export default function NavigationManager() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/admin/navigation").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => {
    load();
  }, []);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...editing, order: parseInt(editing.order, 10) || 0 };
      if (editing.id) {
        await api.put(`/admin/navigation/${editing.id}`, payload);
      } else {
        await api.post("/admin/navigation", payload);
      }
      toast.success("Onglet enregistré.");
      setEditing(null);
      load();
    } catch {
      toast.error("Erreur lors de l'enregistrement.");
    } finally {
      setSaving(false);
    }
  };

  const toggleVisible = async (item) => {
    await api.put(`/admin/navigation/${item.id}`, { ...item, visible: !item.visible });
    load();
  };

  const remove = async (id) => {
    await api.delete(`/admin/navigation/${id}`);
    toast.success("Onglet supprimé.");
    load();
  };

  const set = (key) => (e) => setEditing((s) => ({ ...s, [key]: e.target.value }));

  return (
    <div data-testid="navigation-manager">
      <div className="mb-8 flex items-center justify-between">
        <h2 className="text-lg font-semibold text-ivory">Navigation — {items.length} onglet(s)</h2>
        <button
          onClick={() => setEditing({ ...emptyItem, order: items.length })}
          className="btn-primary !h-10 !px-5 !text-[11px]"
          data-testid="nav-add-button"
        >
          <Plus className="h-4 w-4" strokeWidth={1.5} />
          Ajouter un onglet
        </button>
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm" data-testid="nav-edit-modal">
          <form onSubmit={save} className="w-full max-w-md space-y-4 rounded-lg border border-white/10 bg-coal p-8">
            <div className="flex items-center justify-between">
              <h3 className="text-base font-semibold text-ivory">
                {editing.id ? "Modifier l'onglet" : "Nouvel onglet"}
              </h3>
              <button type="button" onClick={() => setEditing(null)} aria-label="Fermer">
                <X className="h-5 w-5 text-ivory/50 hover:text-ivory" strokeWidth={1.5} />
              </button>
            </div>
            <input required value={editing.label} onChange={set("label")} placeholder="Nom de l'onglet" className="input-dark !h-11" data-testid="nav-label-input" />
            <input required value={editing.path} onChange={set("path")} placeholder="Lien (ex : /carte ou https://…)" className="input-dark !h-11" data-testid="nav-path-input" />
            <div>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-ivory/50">Ordre</label>
              <input type="number" value={editing.order} onChange={set("order")} className="input-dark !h-11 w-28" data-testid="nav-order-input" />
            </div>
            <label className="flex items-center gap-3 text-sm text-ivory/70">
              Visible
              <Switch checked={editing.visible} onCheckedChange={(v) => setEditing((s) => ({ ...s, visible: v }))} data-testid="nav-visible-switch" />
            </label>
            <button type="submit" disabled={saving} className="btn-primary w-full" data-testid="nav-save-button">
              {saving ? "Enregistrement…" : "Enregistrer"}
            </button>
          </form>
        </div>
      )}

      <div className="space-y-2" data-testid="nav-items-list">
        {items.map((item) => (
          <div key={item.id} className="flex items-center gap-4 rounded-lg border border-white/10 bg-coal/60 px-5 py-3.5" data-testid={`nav-row-${item.id}`}>
            <span className="w-10 shrink-0 text-xs text-ivory/35">{item.order}</span>
            <span className={`flex-1 truncate text-sm ${item.visible ? "text-ivory" : "text-ivory/35 line-through"}`}>
              {item.label}
            </span>
            <span className="hidden truncate text-xs text-ivory/40 sm:block">{item.path}</span>
            <button onClick={() => toggleVisible(item)} aria-label={item.visible ? "Masquer" : "Afficher"} className="flex h-8 w-8 items-center justify-center rounded-md border border-white/10 text-ivory/50 transition-colors hover:border-gold hover:text-gold" data-testid={`nav-toggle-${item.id}`}>
              {item.visible ? <Eye className="h-3.5 w-3.5" strokeWidth={1.5} /> : <EyeOff className="h-3.5 w-3.5" strokeWidth={1.5} />}
            </button>
            <button onClick={() => setEditing({ ...item })} aria-label="Modifier" className="flex h-8 w-8 items-center justify-center rounded-md border border-white/10 text-ivory/50 transition-colors hover:border-gold hover:text-gold" data-testid={`nav-edit-${item.id}`}>
              <Pencil className="h-3.5 w-3.5" strokeWidth={1.5} />
            </button>
            <button onClick={() => remove(item.id)} aria-label="Supprimer" className="flex h-8 w-8 items-center justify-center rounded-md border border-white/10 text-ivory/40 transition-colors hover:border-red-400/50 hover:text-red-400" data-testid={`nav-delete-${item.id}`}>
              <Trash2 className="h-3.5 w-3.5" strokeWidth={1.5} />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
